var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
/**
 * Multi-value subfield renderer (TASK-064) — every subfield whose values live in
 * `SubfieldValue.values[]` (`utils/multiValueSubfield.ts`).
 *
 * ONE VALUE = ONE ROW (CZNA-1378, variant C of CZNA-1031). For the shapes a
 * cataloger can type into — FREE-TEXT (`multi-text`, TASK-110) and HYBRID
 * (picker-backed + `allowFreeText`, CZNA-378) — each value is its own
 * {@link RepeatableValueRow}: an input that looks like any single-value subfield,
 * with the vocabulary's suggestions and magnifier when there is a vocabulary, and
 * an ✕. Under the last row "＋ Další <label>" adds another. That covers all seven
 * repeatable subfields of the packaged templates (100/400/670 personal, 670
 * corporate and meeting, 926 author-work).
 *
 * What it replaced, and why: a chip list with a draft input, an "Přidat" button,
 * Enter-to-confirm and a multi-select modal — four ways in, and text in the box
 * that was not yet a value (CZNA-1030 only stopped it being lost on blur). NK
 * could not tell how to use it. A second value is the exception in the data
 * (`100 $c`: 4 of 7 450 occurrences), so rows cost nothing a cataloger does
 * daily; the measurements are in CZNA-1031's comments.
 *
 * The rows ARE `values[]`, blank ones included, so a typed character is in the
 * form state — and therefore in the MARC preview — at once. The codec trims and
 * drops blank entries on the way out (`genericFormToProposalData`), so the stored
 * shape is unchanged: `[{ value, sourceAuthorityId? }]`, order = `subfield_index`.
 * An empty `values[]` renders as one empty row.
 *
 * CZNA-1646 — a REGISTER picker (`multi-record-picker`) is rows too: each value is the
 * subfield's own single picker ({@link RecordPickerSubfield} in its row mode), with its own
 * pick and its own link (`sourceAuthorityId` per entry, CZNA-1314). So a closed register
 * stays closed per value, a code-bearing one stores the code, an open one offers to create a
 * term or propose an authority, and a literal one keeps typed text — whatever the picker did
 * for the one value it used to hold. A multi-line note (`multi-textarea`) keeps its textarea
 * in every row.
 *
 * The CHIP list survives only for the shapes no packaged template uses: a static
 * option list and a vocabulary picker with no free text (closed multi-vocabulary). There
 * nothing can be typed, so the draft problem above never existed, and the multi-select
 * modal still REPLACES the selection on confirm (it pre-checks what is already picked).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Button, IconButton } from '@folio/stripes/components';
import { Controller } from 'react-hook-form';
import { resolveLabel } from '../renderer/labelResolver';
import { useVocabularyTypes } from '../../../hooks/useVocabularyTypes';
import { VOCABULARY_TYPE_CONFIG } from '../../../types/api';
import RecordPickerModal from './RecordPickerModal';
import RecordPickerSubfield from './RecordPickerSubfield';
import RepeatableValueRow from './RepeatableValueRow';
import { subfieldRowStyle as rowStyle, subfieldCodeStyle as codeStyle, subfieldLabelStyle as labelStyle, subfieldLinkButtonStyle, } from './subfieldStyles';
var chipContainerStyle = {
    flex: 1,
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '6px',
    minHeight: '32px',
    padding: '4px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    backgroundColor: '#fafafa',
};
var chipStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 4px 4px 10px',
    borderRadius: '12px',
    backgroundColor: '#e0ecf6',
    color: '#1960a4',
    fontSize: '13px',
    fontWeight: 500,
    lineHeight: 1,
};
var emptyPlaceholderStyle = {
    color: '#888',
    fontSize: '13px',
    fontStyle: 'italic',
    padding: '4px 8px',
};
/** The rows and the "＋ Další" link under them, stacked. */
var rowsColumnStyle = {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
};
var rowsStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
};
/**
 * "Doplněk ke jménu" → "doplněk ke jménu", for "＋ Další doplněk ke jménu". Only the
 * first letter, and not when the second is a capital too — an acronym ("ISBN")
 * keeps its case.
 */
var lowerFirst = function (label) {
    if (label.length > 1 && label[1] === label[1].toLocaleUpperCase('cs') && label[1] !== label[1].toLocaleLowerCase('cs')) {
        return label;
    }
    return label.charAt(0).toLocaleLowerCase('cs') + label.slice(1);
};
/**
 * Builds the static-options list for the picker modal from a `selectOptions`
 * array on the SubfieldDef. The picker keys by `id`, so for static mode we
 * use the option `value` as the id (it is also the value that gets persisted).
 */
var toStaticOptions = function (sf, intl) {
    if (!sf.selectOptions)
        return [];
    return sf.selectOptions.map(function (opt) { return ({
        id: opt.value,
        // `opt.value` doubles as the final-resort fallback so a static-list
        // option without any label / labelKey still renders something
        // recognisable in the picker.
        headingValue: resolveLabel(opt, intl, opt.value),
    }); });
};
var MultiSelectVocabularySubfield = function (_a) {
    var subfieldDef = _a.subfieldDef, pathPrefix = _a.pathPrefix, control = _a.control, inputId = _a.inputId, onValueRemoved = _a.onValueRemoved;
    var intl = useIntl();
    var _b = useState(false), modalOpen = _b[0], setModalOpen = _b[1];
    var isStatic = subfieldDef.staticOptions === true;
    // FREE-TEXT mode (TASK-110): no picker backing. Triggered by
    // `inputType: 'multi-text'` or, defensively, by a multiValue subfield that
    // carries no vocabulary/record/static backing at all.
    var isFreeText = subfieldDef.inputType === 'multi-text'
        // CZNA-1646 — free text too, one textarea per value.
        || subfieldDef.inputType === 'multi-textarea'
        || (!isStatic
            && !subfieldDef.recordType
            && !subfieldDef.vocabularyType
            && subfieldDef.inputType !== 'multi-vocabulary'
            // CZNA-214: a dedicated multi-AUTHORITY picker is never free-text.
            && subfieldDef.inputType !== 'multi-record-picker');
    // CZNA-378 — picker-backed AND free-text-capable: the open-vocabulary shape of
    // ADR-0004, a derived frequency head offered by the register, an unbounded tail
    // the cataloger types.
    //
    // Excludes static-list mode: a typed and a picked static value both persist as a
    // bare `{ value }`, so a row could not show which one it holds. A static list
    // that needs free text should be `multi-text` with the options as a placeholder.
    //
    // Also excludes `multi-record-picker` (CZNA-214): that kind picks real authority
    // records, where an unmatched typed string is not a tail value but a dangling
    // reference.
    var isHybrid = !isFreeText
        && !isStatic
        && subfieldDef.inputType !== 'multi-record-picker'
        && subfieldDef.allowFreeText === true;
    // CZNA-1646 — one register picker per value; see the file comment.
    var isPickerRows = !isStatic && subfieldDef.inputType === 'multi-record-picker';
    var isRows = isFreeText || isHybrid || isPickerRows;
    var staticItems = useMemo(function () { return (isStatic ? toStaticOptions(subfieldDef, intl) : []); }, [isStatic, subfieldDef, intl]);
    // Map static value (= id) → human label so chips show the translated label,
    // not the raw enum value. For API-backed picks the persisted value already
    // is the heading.
    var staticLabelByValue = useMemo(function () {
        var m = new Map();
        staticItems.forEach(function (o) { return m.set(o.id, o.headingValue); });
        return m;
    }, [staticItems]);
    var recordType = subfieldDef.recordType || subfieldDef.vocabularyType || '';
    var subfieldPath = "".concat(pathPrefix, ".").concat(subfieldDef.code);
    // CZNA-1348 — may a row create a term, by the single-value picker's rule
    // (`RecordPickerSubfield`, `supportsAddVocabulary`): an open, non-literal
    // free-text subfield on a type VocabularyForm can create, and a registry that
    // has answered and does not call the type CLOSED. A type with no registry row is
    // not CLOSED, as on the backend, which refuses a CLOSED create from a non-admin
    // with 403 (CZNA-1310).
    var _c = useVocabularyTypes(), vocabularyTypes = _c.vocabularyTypes, vocabularyTypesLoading = _c.isLoading, vocabularyTypesError = _c.isError;
    var vocabularyIsClosed = vocabularyTypes
        .some(function (t) { return t.code === recordType && t.validationType === 'CLOSED'; });
    // CZNA-1470 — a type created in Nastavení → Slovníky has a registry row but no
    // VOCABULARY_TYPE_CONFIG entry; VocabularyForm offers it, so the term may be added.
    var vocabularyIsKnown = Boolean(VOCABULARY_TYPE_CONFIG[recordType])
        || vocabularyTypes.some(function (t) { return t.code === recordType; });
    var supportsAddTerm = isHybrid
        && subfieldDef.literalFreeText !== true
        && vocabularyIsKnown
        && !vocabularyTypesLoading
        && !vocabularyTypesError
        && !vocabularyIsClosed;
    // Stable id for the visible label span so the inputs can be associated with it
    // via aria-labelledby (CZNA-463) — an input otherwise has no accessible name.
    //
    // Distinct from `inputId` and not replaced by it (CZNA-1048): this one names
    // the LABEL, the other names the values.
    var labelId = "".concat(pathPrefix, ".").concat(subfieldDef.code, "-label");
    /** CZNA-1646 — the `idx`-th row's input, the first one being what the caption labels. */
    var rowInputId = function (idx) { return "".concat(inputId, "-value-").concat(idx); };
    var label = resolveLabel(subfieldDef, intl);
    // "＋ Další" moves focus into the row it added; the row exists only after the
    // re-render, so the index waits here until then. Found through the value holder
    // (`inputId`), whose inputs are the rows in order.
    var _d = useState(null), focusRow = _d[0], setFocusRow = _d[1];
    useEffect(function () {
        var _a, _b, _c;
        if (focusRow === null)
            return;
        // By row, not by input: a textarea row has no input, and a picker row's control is the
        // Selection's button (CZNA-1646).
        (_c = (_b = (_a = document.getElementById(inputId)) === null || _a === void 0 ? void 0 : _a.querySelectorAll('[data-test-multi-row]')[focusRow]) === null || _b === void 0 ? void 0 : _b.querySelector('input, textarea, button')) === null || _c === void 0 ? void 0 : _c.focus();
        setFocusRow(null);
    }, [focusRow, inputId]);
    // A key per row that follows the row, not its position: a row holds its own open
    // suggestion list and dialogs, and an index key would hand them to the row below
    // when an earlier one is removed. The stored entries carry no id to key on (the
    // shape is fixed), so the keys live here, in step with `values[]`.
    var rowKeys = useRef([]);
    var nextRowKey = useRef(0);
    var syncRowKeys = function (count) {
        var keys = rowKeys.current;
        while (keys.length < count)
            keys.push(nextRowKey.current++);
        keys.length = count;
        return keys;
    };
    /**
     * CZNA-1646 — the rows as last drawn, and how to write them. A row's write can come late:
     * a picker row reads the picked term's code (`377 $a`), a created term answers from its
     * own dialog. Written through a closure of the render that started it, it would write
     * THAT render's rows back — undoing whatever another row did meanwhile, or bringing back
     * a row removed meanwhile. So a row writes by its key, into the rows as they are now, and
     * a row that is gone writes nothing.
     *
     * Only a REMOVED row writes nothing: a pick whose read answers after the cataloguer moved
     * to another tab (the subfield unmounted, its rows still in the record) still lands. A
     * picker that cancelled its read on unmount lost exactly that pick (CZNA-1320 gate,
     * corporate 377 picked last on its tab).
     */
    var latest = useRef({ rows: [], write: function () { } });
    var changeRowByKey = function (key, entry) {
        var idx = rowKeys.current.indexOf(key);
        if (idx < 0)
            return;
        var next = __spreadArray([], latest.current.rows, true);
        next[idx] = entry;
        latest.current.write(next);
    };
    return (React.createElement(Controller, { name: subfieldPath, control: control, render: function (_a) {
            var field = _a.field;
            var subVal = (field.value || { value: '', values: [] });
            var values = subVal.values || [];
            var setValues = function (next) { return field.onChange(__assign(__assign({}, subVal), { values: next })); };
            var caption = (React.createElement(React.Fragment, null,
                label,
                subfieldDef.required && React.createElement("span", { style: { color: '#d0021b', marginLeft: '2px' } }, "*")));
            var header = (React.createElement(React.Fragment, null,
                !subfieldDef.formOnly && React.createElement("span", { style: codeStyle, "aria-hidden": "true" },
                    "$",
                    subfieldDef.code),
                isFreeText || isHybrid ? (React.createElement("label", { id: labelId, htmlFor: rowInputId(0), style: labelStyle }, caption)) : (React.createElement("span", { id: labelId, style: labelStyle }, caption))));
            if (isRows) {
                // An empty list is shown as one empty row; typing into it creates entry 0.
                var rows_1 = values.length > 0 ? values : [{ value: '' }];
                var keys_1 = syncRowKeys(rows_1.length);
                latest.current = { rows: rows_1, write: setValues };
                var removeRow_1 = function (idx) {
                    var next = __spreadArray([], rows_1, true);
                    next.splice(idx, 1);
                    rowKeys.current.splice(idx, 1);
                    setValues(next);
                    onValueRemoved === null || onValueRemoved === void 0 ? void 0 : onValueRemoved(idx);
                };
                var addRow = function () {
                    setValues(__spreadArray(__spreadArray([], rows_1, true), [{ value: '' }], false));
                    setFocusRow(rows_1.length);
                };
                return (
                // CZNA-962: path-scoped handle, the same the record pickers carry. The
                // code-only `data-test-multi-*` attribute names which shape rendered.
                React.createElement("div", __assign({ style: rowStyle, "data-field-path": subfieldPath }, (isHybrid || isPickerRows
                    ? { 'data-test-multi-vocabulary': subfieldDef.code }
                    : { 'data-test-multi-free-text': subfieldDef.code })),
                    header,
                    React.createElement("div", { style: rowsColumnStyle },
                        React.createElement("div", { id: inputId, style: rowsStyle },
                            isPickerRows && rows_1.map(function (entry, idx) {
                                // Captured now: `keys` is the live list, and shifts when a row is removed.
                                var key = keys_1[idx];
                                return (React.createElement(RecordPickerSubfield, { key: key, subfieldDef: subfieldDef, pathPrefix: pathPrefix, control: control, row: {
                                        valuePath: "".concat(subfieldPath, ".values.").concat(idx),
                                        // An empty `values[]` is drawn as one empty row; its first pick
                                        // creates entry 0.
                                        entry: entry,
                                        onChange: function (next) { return changeRowByKey(key, next); },
                                        index: idx,
                                        onRemove: function () { return removeRow_1(idx); },
                                        removable: rows_1.length > 1 || Boolean(entry.value),
                                    } }));
                            }),
                            !isPickerRows && rows_1.map(function (entry, idx) {
                                // Captured now: `keys` is the live list, and shifts when a row is removed.
                                var key = keys_1[idx];
                                var otherLinkedIds = new Set(rows_1
                                    .filter(function (_, i) { return i !== idx; })
                                    .map(function (r) { return r.sourceAuthorityId; })
                                    .filter(function (x) { return Boolean(x); }));
                                return (React.createElement(RepeatableValueRow, { key: key, entry: entry, index: idx, onChange: function (next) { return changeRowByKey(key, next); }, onRemove: function () { return removeRow_1(idx); }, removable: rows_1.length > 1 || Boolean(entry.value), recordType: isHybrid ? recordType : '', supportsAddTerm: supportsAddTerm, otherLinkedIds: otherLinkedIds, labelId: labelId, rowId: "".concat(pathPrefix, ".").concat(subfieldDef.code, "-").concat(idx), label: label, placeholder: idx === 0 ? subfieldDef.placeholder : undefined, multiline: subfieldDef.inputType === 'multi-textarea', inputElementId: rowInputId(idx) }));
                            })),
                        React.createElement("button", { type: "button", style: subfieldLinkButtonStyle, onClick: addRow, "data-test-multi-more": true }, intl.formatMessage({ id: 'ui-czna-authorities.multiVocabulary.more' }, { label: lowerFirst(label) })))));
            }
            var removeAt = function (idx) {
                var next = __spreadArray([], values, true);
                next.splice(idx, 1);
                setValues(next);
                onValueRemoved === null || onValueRemoved === void 0 ? void 0 : onValueRemoved(idx);
            };
            // Replace strategy: the modal pre-checks the current selection, so the user
            // can both add and remove in one round. Persisted shape:
            //  - static-list: `{ value: <option-value> }` (no FK, no source authority)
            //  - API-backed:  `{ value: <heading>, sourceAuthorityId: <id> }`
            var handleConfirm = function (picked) {
                setValues(picked.map(function (p) { return (isStatic
                    ? { value: p.id }
                    : { value: p.heading, sourceAuthorityId: p.id }); }));
                setModalOpen(false);
            };
            // The initial selection for the modal — for static mode the id is the
            // option's `value`; for API mode the id is `sourceAuthorityId`.
            var initialSelectedIds = values
                .map(function (v) { return (isStatic ? v.value : v.sourceAuthorityId); })
                .filter(function (x) { return !!x; });
            // CZNA-378 — the modal can only resolve an id to a heading from rows it has
            // rendered, and it drops selections it cannot resolve; a vocabulary longer
            // than one page would lose a previous pick on the next confirm. So hand it
            // the headings we already hold.
            var initialSelectedHeadings = {};
            values.forEach(function (v) {
                if (v.sourceAuthorityId)
                    initialSelectedHeadings[v.sourceAuthorityId] = v.value;
            });
            return (React.createElement("div", { style: rowStyle, "data-field-path": subfieldPath, "data-test-multi-vocabulary": subfieldDef.code },
                header,
                React.createElement("div", { id: inputId, style: chipContainerStyle },
                    values.length > 0
                        ? values.map(function (entry, idx) { return (React.createElement("span", { key: "".concat(entry.value, "-").concat(idx), style: chipStyle, "data-test-multi-chip": true },
                            React.createElement("span", null, isStatic ? (staticLabelByValue.get(entry.value) || entry.value) : entry.value),
                            React.createElement(IconButton, { icon: "times-circle-solid", onClick: function () { return removeAt(idx); }, "aria-label": intl.formatMessage({ id: 'ui-czna-authorities.multiVocabulary.remove' }), "data-test-multi-chip-remove": true }))); })
                        : (React.createElement("span", { style: emptyPlaceholderStyle }, intl.formatMessage({ id: 'ui-czna-authorities.multiVocabulary.empty' }))),
                    React.createElement(Button, { onClick: function () { return setModalOpen(true); }, marginBottom0: true, "data-test-multi-vocabulary-add": true }, intl.formatMessage({ id: 'ui-czna-authorities.multiVocabulary.add' }))),
                modalOpen && (React.createElement(RecordPickerModal, { open: modalOpen, onClose: function () { return setModalOpen(false); }, recordType: recordType, multiSelect: true, initialSelectedIds: initialSelectedIds, initialSelectedHeadings: initialSelectedHeadings, onSelectMany: handleConfirm, staticOptions: isStatic ? staticItems : undefined }))));
        } }));
};
export default MultiSelectVocabularySubfield;
